import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  confirm,
  confirmRole,
  error as errorSection,
  nonAuthRep,
  welcome,
} from '@services/sections/lib/accountSetup'
import { homeSection } from '@services/sections/lib/home'
import {
  l3Onboarding,
  l3OnboardingOverview,
} from '@services/sections/lib/onboarding'
import { useAccountSetupInfoQuery } from '@shared/graphql'
import { useRouter } from 'next/router'

import { replaceOperationIdInRoute } from '../../../l3-onboarding/constants/routes'

import { AccountSetupContext } from './context'

import type {
  AccountSetupContextActions,
  AccountSetupContextState,
} from './context'

export type AccountSetupProviderProps = React.PropsWithChildren<{
  /**
   * Initial values of the internal state for testing.
   */
  initValues?: {
    loading: {
      rerouting: boolean
      processing: boolean
    }
  }
}>

export const AccountSetupProvider: React.FC<AccountSetupProviderProps> = ({
  children,
  initValues,
}) => {
  const router = useRouter()
  const [businessDetails, setBusinessDetails] =
    useState<AccountSetupContextState['data']['businessDetails']>()
  const [loading, setLoading] = useState(
    initValues?.loading ?? {
      rerouting: true,
      processing: false,
    },
  )

  const { data, refetch, error } = useAccountSetupInfoQuery({
    notifyOnNetworkStatusChange: true,
    onError: () => void router.push(errorSection.route),
    onCompleted: ({ accountSetupInfo }) => {
      const {
        isCOPBusinessOnboardingInProgress,
        operationId,
        isRolrUser,
        hasCompletedAllVerifications,
        isNonAuthorizedRep,
        hasConfirmedDetails,
      } = accountSetupInfo

      if (isCOPBusinessOnboardingInProgress || isRolrUser) {
        if (operationId == null) {
          return void router.push(l3Onboarding.route)
        }

        return void router.push(
          replaceOperationIdInRoute(l3OnboardingOverview.route, operationId),
        )
      }

      // If it has completed all verifications
      // Reroute them to home section
      if (hasCompletedAllVerifications) {
        return void router.push(homeSection.route)
      }

      // If we haven't completed KYB, and user is not the authorized rep;
      // reroute user to non-authorized rep firewall
      if (isNonAuthorizedRep) {
        return void router.push(nonAuthRep.route)
      }

      // If the user has confirmed their business details and is the authorized rep,
      // they should already have a COP application at this point.
      // Reaching this block indicates that something may have gone wrong in our backend, so redirect to error page.
      if (hasConfirmedDetails) {
        return void router.push(errorSection.route)
      }

      void router.push(welcome.route)
    },
  })

  /**
   * React to router changes.
   */
  useEffect(() => {
    // Ensure steps occur linearly [https://nextjs.org/docs/api-reference/next/router#routerbeforepopstate}]
    router.beforePopState(({ as }) => {
      if (as !== router.asPath) {
        void router.push(router.asPath)
        return false
      }
      return true
    })

    // After initial route change, set rerouting=false
    const handleInitialReroute = () => {
      if (loading.rerouting) {
        setLoading((prev) => ({ ...prev, rerouting: false }))
      }
    }

    router.events.on('routeChangeComplete', handleInitialReroute)

    return () => {
      router.events.off('routeChangeComplete', handleInitialReroute)
    }
  }, [loading.rerouting, router])

  /**
   * Determine next step based completed step.
   */
  const completeStep = useCallback<AccountSetupContextActions['completeStep']>(
    (action) => {
      const { isOnboardingInitiator } = data?.accountSetupInfo ?? {}

      switch (action) {
        case 'WELCOME':
          // Next step is to confirm business details
          return void router.push(confirm.route)

        case 'INVITE_AUTH_REP':
          return void router.push(nonAuthRep.route)

        case 'CONFIRM_DETAILS':
          // If a user is not the onboarding initiator, we already know role as AR
          if (!isOnboardingInitiator) {
            return completeStep('CONFIRM_ROLE')
          }
          // By default, ask user to confirm role
          return void router.push(confirmRole.route)

        case 'CONFIRM_ROLE':
          // Refetch after prequal auto approval
          void refetch()
          // Enter processing state when all the pre-qual steps is completed
          setLoading((prev) => ({ ...prev, processing: true }))
      }
    },
    [data?.accountSetupInfo, refetch, router],
  )

  const value = useMemo<[AccountSetupContextState, AccountSetupContextActions]>(
    () => [
      {
        error,
        loading,
        data: {
          ...data?.accountSetupInfo,
          businessDetails,
        },
      },
      {
        completeStep,
        setBusinessDetails,
      },
    ],
    [error, loading, data?.accountSetupInfo, businessDetails, completeStep],
  )

  return (
    <AccountSetupContext.Provider value={value}>
      {children}
    </AccountSetupContext.Provider>
  )
}
